import { type ApolloError, useQuery } from "@apollo/client";
import type { OnChangeFn, SortingState } from "@tanstack/react-table";
import React from "react";
import { useTranslation } from "react-i18next";

import { INVOICE_OVERVIEW_CONFIG_QUERY } from "@/apis/gql/apolloClient";
import { UsageHelperBanner } from "@/components/shared/banners/usage-helper-banner";
import type { InvoiceType } from "@/lib/constants/invoice-type";
import { InvoiceStatus } from "@/lib/constants/invoiceStatus";
import { useInvoicesContext } from "@/providers/invoices";
import { useDeleteInvoice } from "@/queries/invoices/delete";
import {
  useUpdateInvoice,
  useUpdateInvoicesStatus,
  useUpdateInvoicesType,
} from "@/queries/invoices/update";

import type { ServerFilters } from "@/hooks/useQueryFilters";
import type { InvoiceTypeString } from "@/lib/constants/invoice-type";
import type { InvoiceFilterFields } from "@/types/utils";
import { DocumentSortingDataTable as DocumentSortingDataTableComponent } from "./document-sorting-data-table";
import { DocumentSortingTableInstanceProvider } from "./table-instance-provider";

type Props = {
  tableName: string;
  filtersConfig?: {
    showStatusFilter: boolean;
    showExportsFilter: boolean;
    showUploadButton: boolean;
    showAccountFilter: boolean;
  };
  actionsConfig?: {
    exportButton: {
      isVisible: boolean;
      options: string[];
    };
  };
  totalItems: number;
  currentPage: number;
  totalPages: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  filters: any;
  setFilters: (filters: any) => void;
  globalSearch: string;
  setGlobalSearch: (globalSearch: string) => void;
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
  error: ApolloError | undefined;
  invoiceType?: InvoiceTypeString;
  resetFilters: () => void;
  initialServerSideFilters: ServerFilters<InvoiceFilterFields>;
  serverSideFilters: ServerFilters<InvoiceFilterFields>;
  globalServerSideSearch: string;
};

export const DocumentSorting = ({
  tableName,
  filtersConfig = {
    showStatusFilter: false,
    showExportsFilter: false,
    showUploadButton: false,
    showAccountFilter: false,
  },
  totalItems,
  currentPage,
  totalPages,
  pageSize,
  onPageChange,
  onPageSizeChange,
  filters,
  setFilters,
  globalSearch,
  setGlobalSearch,
  sorting,
  onSortingChange,
  error,
  invoiceType,
  resetFilters,
  initialServerSideFilters,
  serverSideFilters,
  globalServerSideSearch,
}: Props) => {
  const { t } = useTranslation();
  const { loading, invoices } = useInvoicesContext();
  const updateInvoiceStatus = useUpdateInvoicesStatus();
  const [updateInvoice] = useUpdateInvoice();
  const [updateInvoiceType] = useUpdateInvoicesType();

  const deleteInvoices = useDeleteInvoice();

  const { data: config } = useQuery(INVOICE_OVERVIEW_CONFIG_QUERY);

  const handleReview = (invoiceIds: string[]) => {
    updateInvoiceStatus(invoiceIds, InvoiceStatus.REVIEWED);
  };

  const handleDelete = (invoiceIds: string[]) => {
    deleteInvoices(invoiceIds);
  };

  const handleTypeChange = (invoiceIds: string[], type: InvoiceType) => {
    if (invoiceType === type) return;
    updateInvoiceType(invoiceIds, type);
  };

  const handleIsABooking = async (invoiceId: string) => {
    await updateInvoice({ invoiceId, isBooking: true });
  };

  return (
    <DocumentSortingTableInstanceProvider
      data={invoices}
      tableName={tableName}
      onRowReview={handleReview}
      onRowDelete={handleDelete}
      onRowIsABooking={handleIsABooking}
      sorting={sorting}
      onSortingChange={onSortingChange}
      filters={filters}
      initialServerSideFilters={initialServerSideFilters}
      serverSideFilters={serverSideFilters}
      resetFilters={resetFilters}
      globalServerSideSearch={globalServerSideSearch}
    >
      <div className="flex flex-col h-full gap-4">
        <UsageHelperBanner
          label={
            invoiceType === "receivable"
              ? t("todos.receivable.sort_explainer")
              : t("todos.payable.sort_explainer")
          }
          link="https://tabula-accounting.notion.site/Wie-funktioniert-der-Pr-fschritt-Sonstige-Dokumente-27beecb29d04802db192d78e67ca39d2?pvs=74"
          linkText={t("todos.sort_help_link")}
        />
        <DocumentSortingDataTableComponent
          loading={loading}
          config={config}
          filtersConfig={filtersConfig}
          onFilter={() => {}}
          onReview={handleReview}
          onDelete={handleDelete}
          onTypeChange={handleTypeChange}
          totalItems={totalItems}
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          filters={filters}
          setFilters={setFilters}
          globalSearch={globalSearch}
          setGlobalSearch={setGlobalSearch}
          error={error}
          invoiceType={invoiceType}
        />
      </div>
    </DocumentSortingTableInstanceProvider>
  );
};
